/** Browser sessions store a nonsecret marker; Passport tokens live in HttpOnly cookies.
 * Existing local Bearer tokens are upgraded by the first authenticated API response.
 */

const KEYS = {
  accessToken: 'access_token',
  tenantId: 'tenantId',
  tokenExpiry: 'token_expiry',
  // Vestigial: kept only so clearAuthData() removes keys left behind by older
  // builds. There is NO refresh-token flow — the tenant token is a Passport
  // personal access token, and services/api.ts redirects to /login on 401.
  refreshToken: 'refresh_token',
  isSuperAdmin: 'is_super_admin',
  tenantSuspended: 'tenant_suspended',
  tenantExpired: 'tenant_expired',
} as const

// --- Read -------------------------------------------------------------------

export function getAccessToken(): string | null {
  return localStorage.getItem(KEYS.accessToken)
}

export function getTenantId(): string | null {
  return localStorage.getItem(KEYS.tenantId)
}

export function getTokenExpiry(): number | null {
  const raw = localStorage.getItem(KEYS.tokenExpiry)
  if (!raw) return null
  const expiry = Number(raw)
  // A corrupted/hand-edited value parses as NaN, which is NOT null — the
  // api.ts request interceptor guards with `expiresAt !== null && Date.now() >=
  // expiresAt`, and every NaN comparison is false, so a dead token kept
  // sailing past the proactive-expiry teardown. Treat it as "no known
  // expiry" and let the server answer 401 instead.
  return Number.isFinite(expiry) ? expiry : null
}

export function isTenantSuspended(): boolean {
  return localStorage.getItem(KEYS.tenantSuspended) === 'true'
}

export function isTenantExpired(): boolean {
  return localStorage.getItem(KEYS.tenantExpired) === 'true'
}

// --- Write ------------------------------------------------------------------

export function setAccessToken(token: string): void {
  localStorage.setItem(KEYS.accessToken, token)
}

/** @deprecated Credentials are written only by the server. Kept for old callers. */
export function setPosTokenCookie(_token: string, _maxAgeSeconds?: number): void {
  // Intentionally no document.cookie write, including for legacy token values.
}

export function clearPosTokenCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = 'POS_token=; path=/; max-age=0; SameSite=Lax'
  document.cookie = 'POS_CSRF=; path=/; max-age=0; SameSite=Lax'
}

export function setTenantId(id: string): void {
  localStorage.setItem(KEYS.tenantId, id)
}

export function setTokenExpiry(expiryMs: number): void {
  localStorage.setItem(KEYS.tokenExpiry, String(expiryMs))
}

export function setTenantSuspended(value: boolean): void {
  localStorage.setItem(KEYS.tenantSuspended, String(value))
}

export function setTenantExpired(value: boolean): void {
  localStorage.setItem(KEYS.tenantExpired, String(value))
}

// --- Clear ------------------------------------------------------------------

export function clearAuthData(): void {
  localStorage.removeItem(KEYS.accessToken)
  localStorage.removeItem(KEYS.refreshToken)
  localStorage.removeItem(KEYS.tokenExpiry)
  localStorage.removeItem(KEYS.tenantId)
  localStorage.removeItem(KEYS.isSuperAdmin)
  localStorage.removeItem(KEYS.tenantSuspended)
  localStorage.removeItem(KEYS.tenantExpired)

  // Server logout expires HttpOnly credentials. Local teardown also removes
  // the readable CSRF binding and any remaining legacy compatibility cookie.
  clearPosTokenCookie()
}

/**
 * Clears tenant-context storage (tenantId + suspension flag) without
 * touching auth tokens. Mirrors the tenant slice's reset semantics.
 */
export function clearTenantContextStorage(): void {
  localStorage.removeItem(KEYS.tenantId)
  localStorage.removeItem(KEYS.tenantSuspended)
  localStorage.removeItem(KEYS.tenantExpired)
}

/**
 * redux-persist entry keys that may hold an `auth` slice. The main app store
 * uses `persist:root`; the login entry uses `persist:auth-root`.
 */
const PERSIST_AUTH_KEYS = ['persist:root', 'persist:auth-root'] as const

/**
 * Drop the persisted `auth` slice on a forced 401 teardown WITHOUT touching the
 * rest of the persisted state (`posSession` — the cashier's open ticket — must
 * survive a token-expiry redirect). `clearAuthData()` only clears the token
 * keys above, so without this the persisted auth (user PII + isAuthenticated)
 * outlived the session it belonged to.
 */
export function clearPersistedAuthSlices(): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  for (const key of PERSIST_AUTH_KEYS) {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) {
        continue
      }
      const parsed = JSON.parse(raw) as Record<string, unknown>
      if (!('auth' in parsed)) {
        continue
      }
      delete parsed.auth
      localStorage.setItem(key, JSON.stringify(parsed))
    } catch {
      // A corrupt/unparseable persist entry is not worth failing the teardown
      // over; tokenStorage is already cleared by the caller.
    }
  }
}
